import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Building2 } from "lucide-react";
import { updateCompany } from "../../api/platform";
import { NeuCard } from "../../components/neu/Neu";
import { usePerms } from "../../hooks/usePerms";
import { CompanyFeatures } from "./CompanyFeatures";
import { ArchiveCompanyDialog } from "./ArchiveCompanyDialog";
import { CompanyActions } from "./CompanyActions";
import { CompanyReceipts } from "./CompanyReceipts";

const TONE = {
  active: "text-success",
  trial: "text-primary",
  suspended: "text-destructive",
  expired: "text-muted-foreground",
};

export const CompanyCard = ({ company, reload }) => {
  const { t } = useTranslation();
  const canManage = usePerms().can("companies");
  const [archiveOpen, setArchiveOpen] = useState(false);

  const patch = (body) => updateCompany(company.id, body).then(reload);

  return (
    <NeuCard className="animate-rise-in p-5" data-testid={`company-card-${company.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="truncate text-lg font-bold">{company.name}</p>
          <p className="font-mono text-xs text-muted-foreground">
            {company.slug} · {company.plan}
          </p>
          <p
            className="mt-1 font-mono text-xs font-bold tracking-widest text-foreground num"
            data-testid={`company-serial-${company.id}`}
          >
            #{company.serial || "—"}
          </p>
          <p className={`mt-1 text-xs font-bold ${TONE[company.status]}`} data-testid={`company-status-${company.id}`}>
            {t(`platform.statuses.${company.status}`)}
          </p>
        </div>
        <Building2 className="h-5 w-5 text-primary" />
      </div>

      <dl className="mt-4 grid grid-cols-2 gap-3 font-mono text-xs">
        <Item label={t("dashboard.employees")} value={`${company.usage.employees}/${company.max_employees}`} />
        <Item label={t("branches.title")} value={`${company.usage.branches}/${company.max_branches}`} />
        <Item label={t("platform.verifications")} value={company.usage.face_verifications_month} />
        <Item label={t("platform.pendingRequests")} value={company.usage.pending_requests} />
        <Item label={t("platform.renewal")} value={company.renewal_date || "—"} />
      </dl>

      <CompanyActions
        company={company}
        patch={patch}
        onArchive={() => setArchiveOpen(true)}
        reload={reload}
        visible={canManage}
      />
      <CompanyFeatures companyId={company.id} />
      <CompanyReceipts companyId={company.id} />
      <ArchiveCompanyDialog
        company={company}
        open={archiveOpen}
        onOpenChange={setArchiveOpen}
        reload={reload}
      />
    </NeuCard>
  );
};

const Item = ({ label, value }) => (
  <div>
    <dt className="text-[10px] tracking-normal text-muted-foreground">{label}</dt>
    <dd className="mt-0.5 font-semibold">{value}</dd>
  </div>
);
